/**
 * Matrix UI Component
 * 
 * Renders configuration matrix as an interactive grid similar to flight booking interfaces.
 * X-axis: horizontal margins (tightest to loosest)  
 * Y-axis: vertical margins (tightest to loosest)
 */

/**
 * Create and render the matrix component
 * @param {Object} matrixData - Matrix data from transformToMatrix()
 * @param {Object} formData - Original form data for units
 * @param {HTMLElement} container - Container element to render into
 * @returns {Object} Matrix component with methods for interaction
 */
export function createMatrixComponent(matrixData, formData, container) {
  const { matrix, xAxis, yAxis, dimensions } = matrixData;
  const { unit } = formData;
  
  // Clear container
  container.innerHTML = '';
  
  // Create main matrix wrapper
  const matrixWrapper = document.createElement('div');
  matrixWrapper.className = 'matrix-wrapper';
  
  // Create matrix container with CSS Grid
  const matrixGrid = document.createElement('div');
  matrixGrid.className = 'matrix-grid';
  matrixGrid.setAttribute('role', 'grid');
  matrixGrid.setAttribute('aria-label', 'Configuration matrix');
  // Pass dynamic grid sizing via CSS variables consumed by styles
  matrixGrid.style.setProperty('--cols', String(dimensions.width));
  matrixGrid.style.setProperty('--rows', String(dimensions.height));
  
  // Create corner cell (empty)
  const cornerCell = document.createElement('div');
  cornerCell.className = 'matrix-corner';
  cornerCell.textContent = unit;
  matrixGrid.appendChild(cornerCell);
  
  // Create X-axis header (horizontal margins)
  xAxis.forEach((xItem, xIndex) => {
    const headerCell = document.createElement('div');
    headerCell.className = 'matrix-x-header';
    headerCell.setAttribute('role', 'columnheader');
    
    headerCell.innerHTML = `
      <div class="margin-value">${xItem.label}</div>
    `;
    
    matrixGrid.appendChild(headerCell);
  });
  
  // Create Y-axis headers and matrix cells
  yAxis.forEach((yItem, yIndex) => {
    // Y-axis header (vertical margin)
    const yHeaderCell = document.createElement('div');
    yHeaderCell.className = 'matrix-y-header';
    yHeaderCell.setAttribute('role', 'rowheader');
    
    yHeaderCell.innerHTML = `
      <div class="margin-value">${yItem.label}</div>
    `;
    
    matrixGrid.appendChild(yHeaderCell);
    
    // Create matrix cells for this row
    xAxis.forEach((xItem, xIndex) => {
      const cell = matrixData.getCellContent(xIndex, yIndex);
      const matrixCell = createMatrixCell(cell, xIndex, yIndex, unit, formData);
      matrixGrid.appendChild(matrixCell);
    });
  });
  
  matrixWrapper.appendChild(matrixGrid);
  
  // Add matrix info panel
  const infoPanel = createInfoPanel(matrixData, unit);
  matrixWrapper.appendChild(infoPanel);
  
  container.appendChild(matrixWrapper);
  
  // Hover callback reference
  let onCellHoverCallback = null;
  let onHoverEndCallback = null;

  // Mouseover to report hovered cell (does not change selection)
  matrixGrid.addEventListener('mouseover', (e) => {
    const cell = e.target.closest('.matrix-cell');
    if (cell && !cell.classList.contains('empty-cell')) {
      const xIndex = parseInt(cell.dataset.x);
      const yIndex = parseInt(cell.dataset.y);
      if (onCellHoverCallback) {
        onCellHoverCallback(xIndex, yIndex, cell);
      }
    } else {
      // Not hovering a valid cell anymore: revert to selected or empty
      if (onHoverEndCallback) onHoverEndCallback();
    }
  });
  // When pointer leaves a cell to a non-cell, signal hover end
  matrixGrid.addEventListener('mouseout', (e) => {
    const fromCell = e.target.closest?.('.matrix-cell');
    const toCell = e.relatedTarget && e.relatedTarget.closest ? e.relatedTarget.closest('.matrix-cell') : null;
    if (fromCell && !toCell) {
      if (onHoverEndCallback) onHoverEndCallback();
    }
  });
  // Fallback: pointer leaves the grid entirely
  matrixGrid.addEventListener('mouseleave', () => {
    if (onHoverEndCallback) onHoverEndCallback();
  });

  // Keyboard navigation within grid (basic arrows + Enter)
  matrixGrid.addEventListener('keydown', (e) => {
    const focusCell = e.target.closest('.matrix-cell');
    const selected = matrixGrid.querySelector('.matrix-cell.selected');
    const activeCell = (focusCell && !focusCell.classList.contains('empty-cell')) ? focusCell : selected;

    if (!activeCell) return;
    const x = parseInt(activeCell.dataset.x);
    const y = parseInt(activeCell.dataset.y);

    let nextX = x;
    let nextY = y;
    if (e.key === 'ArrowRight') nextX = Math.min(dimensions.width - 1, x + 1);
    else if (e.key === 'ArrowLeft') nextX = Math.max(0, x - 1);
    else if (e.key === 'ArrowDown') nextY = Math.min(dimensions.height - 1, y + 1);
    else if (e.key === 'ArrowUp') nextY = Math.max(0, y - 1);
    else if (e.key === 'Enter' || e.key === ' ') {
      // Trigger click/select
      const evt = new MouseEvent('click', { bubbles: true });
      activeCell.dispatchEvent(evt);
      e.preventDefault();
      return;
    } else {
      return;
    }

    if (nextX !== x || nextY !== y) {
      selectMatrixCell(matrixGrid, nextX, nextY);
      const toFocus = matrixGrid.querySelector(`.matrix-cell[data-x="${nextX}"][data-y="${nextY}"]`);
      if (toFocus) toFocus.focus();
      e.preventDefault();
    }
  });

  // Return component interface
  return {
    element: matrixWrapper,
    selectCell: (xIndex, yIndex) => selectMatrixCell(matrixGrid, xIndex, yIndex),
    getSelectedCell: () => getSelectedCell(matrixGrid),
    onCellSelect: (callback) => addCellSelectListener(matrixGrid, callback),
    onCellHover: (callback) => { onCellHoverCallback = callback; },
    onHoverEnd: (callback) => { onHoverEndCallback = callback; },
    refresh: (newMatrixData, newFormData) => {
      return createMatrixComponent(newMatrixData, newFormData, container);
    }
  };
}

/**
 * Create individual matrix cell
 * @param {Object|null} cellData - Cell content data or null if empty
 * @param {number} xIndex - X coordinate
 * @param {number} yIndex - Y coordinate  
 * @param {string} unit - Unit string for display
 * @returns {HTMLElement} Matrix cell element
 */
function createMatrixCell(cellData, xIndex, yIndex, unit, formData) {
  const cell = document.createElement('div');
  cell.className = 'matrix-cell';
  cell.dataset.x = xIndex;
  cell.dataset.y = yIndex;
  cell.setAttribute('role', 'gridcell');
  // Make interactive cells focusable for keyboard navigation
  cell.tabIndex = 0;
  
  if (cellData && cellData.count > 0) {
    // Populated cell
    // Draw a scaled preview of page and content using the primary configuration
    const preview = createLayoutPreview(cellData.primaryConfig, formData);
    cell.appendChild(preview);
    // Hover visuals are entirely handled in CSS
  } else {
    // Empty cell
    cell.classList.add('empty-cell');
    // Avoid focusing empty/inactive cells
    cell.tabIndex = -1;
  }
  
  return cell;
}

/**
 * Create a miniature layout preview (SVG) scaled to the page aspect ratio
 * Page rendered as light gray, content area as darker gray
 * @param {Object} config - Grid configuration (primaryConfig)
 * @param {Object} formData - Includes pageWidth/pageHeight
 * @returns {HTMLElement}
 */
function createLayoutPreview(config, formData) {
  const { pageWidth, pageHeight } = formData;
  const left = config.leftMargin;
  const top = config.topMargin;
  const right = config.rightMargin ?? left;
  const bottom = config.bottomMargin ?? top;

  const contentW = Math.max(0, pageWidth - left - right);
  const contentH = Math.max(0, pageHeight - top - bottom);

  // Canvas size inside the cell
  const box = 52; // px
  const pad = 4;  // inner padding

  const scale = Math.min(
    (box - pad * 2) / pageWidth,
    (box - pad * 2) / pageHeight
  );

  const pageW = Math.max(1, pageWidth * scale);
  const pageH = Math.max(1, pageHeight * scale);
  const x0 = (box - pageW) / 2;
  const y0 = (box - pageH) / 2;

  const contentX = x0 + left * scale;
  const contentY = y0 + top * scale;
  const contentWpx = Math.max(0, contentW * scale);
  const contentHpx = Math.max(0, contentH * scale);

  const wrapper = document.createElement('div');
  wrapper.className = 'matrix-preview flex items-center justify-center w-full h-full';
  wrapper.innerHTML = `
    <svg width="${box}" height="${box}" viewBox="0 0 ${box} ${box}" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <!-- Page -->
      <rect x="${x0.toFixed(2)}" y="${y0.toFixed(2)}" width="${pageW.toFixed(2)}" height="${pageH.toFixed(2)}"
            fill="#e5e7eb" stroke="#cbd5e1" stroke-width="0.75" rx="3"/>
      <!-- Content -->
      <rect x="${contentX.toFixed(2)}" y="${contentY.toFixed(2)}" width="${contentWpx.toFixed(2)}" height="${contentHpx.toFixed(2)}"
            fill="#cbd5e1" stroke="#94a3b8" stroke-width="0.6" rx="2"/>
    </svg>
  `;
  return wrapper;
}

/**
 * Create information panel for matrix
 * @param {Object} matrixData - Matrix data structure
 * @param {string} unit - Unit string
 * @returns {HTMLElement} Info panel element
 */
function createInfoPanel(matrixData, unit) {
  const panel = document.createElement('div');
  panel.className = 'matrix-info-panel';
  
  panel.innerHTML = `
    <div class="matrix-legend">
      <span>
        Select a cell to view configuration details
      </span>
    </div>
  `;
  
  return panel;
}

/**
 * Select a specific matrix cell
 * @param {HTMLElement} matrixGrid - Matrix grid element
 * @param {number} xIndex - X coordinate
 * @param {number} yIndex - Y coordinate
 */
function selectMatrixCell(matrixGrid, xIndex, yIndex) {
  // Clear previous selection
  matrixGrid.querySelectorAll('.matrix-cell').forEach(cell => {
    cell.classList.remove('selected');
    cell.setAttribute('aria-selected', 'false');
  });
  
  // Select new cell
  const targetCell = matrixGrid.querySelector(`[data-x="${xIndex}"][data-y="${yIndex}"]`);
  if (targetCell && !targetCell.classList.contains('empty-cell')) {
    targetCell.classList.add('selected');
    targetCell.setAttribute('aria-selected', 'true');
  }
}

/**
 * Clear any current selection without selecting a new one
 * @param {HTMLElement} matrixGrid
 */
function clearMatrixSelection(matrixGrid) {
  matrixGrid.querySelectorAll('.matrix-cell').forEach(cell => {
    cell.classList.remove('selected');
    cell.setAttribute('aria-selected', 'false');
  });
}

/**
 * Get currently selected cell coordinates
 * @param {HTMLElement} matrixGrid - Matrix grid element
 * @returns {Object|null} Selected cell coordinates or null
 */
function getSelectedCell(matrixGrid) {
  const selected = matrixGrid.querySelector('.matrix-cell.selected');
  if (selected) {
    return {
      x: parseInt(selected.dataset.x),
      y: parseInt(selected.dataset.y)
    };
  }
  return null;
}

/**
 * Add cell selection event listener
 * @param {HTMLElement} matrixGrid - Matrix grid element
 * @param {Function} callback - Callback function (xIndex, yIndex, cellData)
 */
function addCellSelectListener(matrixGrid, callback) {
  matrixGrid.addEventListener('click', (e) => {
    const cell = e.target.closest('.matrix-cell');
    if (cell && !cell.classList.contains('empty-cell')) {
      const xIndex = parseInt(cell.dataset.x);
      const yIndex = parseInt(cell.dataset.y);

      // Toggle behavior: if already selected, unselect and notify
      if (cell.classList.contains('selected')) {
        clearMatrixSelection(matrixGrid);
        if (callback) {
          callback(null, null, null);
        }
        return;
      }

      // Select the cell visually
      selectMatrixCell(matrixGrid, xIndex, yIndex);

      // Call the callback
      if (callback) {
        callback(xIndex, yIndex, cell);
      }
    }
  });
}
